import type { SocialLinks, ImageFile, Track } from './trackTypes';

import { apiClient, uploadFile } from './apiClient';
import { API_CONFIG } from '../config/api';
import { logError, logInfo } from './logtail';

export const getTracks = async (): Promise<Track[]> => {
  try {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.TRACKS);
    logInfo('Fetched tracks from backend', { count: Array.isArray(response.data) ? response.data.length : undefined });
    return response.data;
  } catch (error) {
    logError('Failed to fetch tracks', { error });
    throw error;
  }
};

export const getTrackById = async (id: string): Promise<Track> => {
  try {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.TRACK_BY_ID(id));
    logInfo('Fetched track by ID', { id });
    return response.data;
  } catch (error) {
    logError('Failed to fetch track by ID', { id, error });
    throw error;
  }
};

export const createTrack = async (track: Omit<Track, 'id'>): Promise<Track> => {
  try {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.TRACKS, track);
    logInfo('Created new track', { name: track.name, artist: track.artist });
    // Compose the returned Track object with the new ID
    return { ...track, id: response.data.trackId };
  } catch (error) {
    logError('Failed to create track', { track, error });
    throw error;
  }
};

export const updateTrack = async (track: Track): Promise<Track> => {
  try {
    const response = await apiClient.put(API_CONFIG.ENDPOINTS.TRACK_BY_ID(track.id), track);
    logInfo('Updated track', { id: track.id });
    return response.data;
  } catch (error) {
    logError('Failed to update track', { id: track.id, error });
    throw error;
  }
};

export const updateTrackMetadata = async (id: string, metadata: Partial<Track>): Promise<Track> => {
  try {
    const response = await apiClient.patch(API_CONFIG.ENDPOINTS.TRACK_METADATA(id), metadata);
    logInfo('Updated track metadata', { id });
    return response.data;
  } catch (error) {
    logError('Failed to update track metadata', { id, error });
    throw error;
  }
};

export const deleteTrack = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(API_CONFIG.ENDPOINTS.TRACK_BY_ID(id));
    logInfo('Deleted track', { id });
  } catch (error) {
    logError('Failed to delete track', { id, error });
    throw error;
  }
};

export const uncollaborate = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(API_CONFIG.ENDPOINTS.UNCOLLABORATE(id));
    logInfo('Uncollaborated from track', { id });
  } catch (error) {
    logError('Failed to uncollaborate from track', { id, error });
    throw error;
  }
};

export const deleteAudioFile = async (trackId: string, fileId: string): Promise<void> => {
  try {
    await apiClient.delete(API_CONFIG.ENDPOINTS.DELETE_AUDIO(trackId, fileId));
    logInfo('Deleted audio file', { trackId, fileId });
  } catch (error) {
    logError('Failed to delete audio file', { trackId, fileId, error });
    throw error;
  }
};

export const deleteVideoFile = async (trackId: string, fileId: string): Promise<void> => {
  try {
    await apiClient.delete(API_CONFIG.ENDPOINTS.DELETE_VIDEO(trackId, fileId));
    logInfo('Deleted video file', { trackId, fileId });
  } catch (error) {
    logError('Failed to delete video file', { trackId, fileId, error });
    throw error;
  }
};

export const deleteFootageFile = async (trackId: string, fileId: string): Promise<void> => {
  try {
    await apiClient.delete(API_CONFIG.ENDPOINTS.DELETE_FOOTAGE(trackId, fileId));
    logInfo('Deleted footage file', { trackId, fileId });
  } catch (error) {
    logError('Failed to delete footage file', { trackId, fileId, error });
    throw error;
  }
};

export const uploadAudio = async (trackId: string, file: File, onProgress?: (progress: number) => void): Promise<{ uploadId: string, message: string }> => {
  try {
    // Ensure WebSocket connection is established for upload notifications
    const { getAuth } = await import('firebase/auth');
    const { asyncUploadService } = await import('./asyncUploadService');
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      await asyncUploadService.connect(user.uid).catch(err => {
        logError('Failed to establish WebSocket connection for upload notifications', { error: err, userId: user.uid });
        // Continue with upload even if WebSocket fails - user just won't get real-time notifications
      });
    }
    
    const response = await uploadFile(API_CONFIG.ENDPOINTS.UPLOAD_AUDIO(trackId), file, onProgress);
    logInfo('Audio upload queued successfully', { trackId, fileName: file.name, uploadId: response.uploadId, message: response.message });
    return { uploadId: response.uploadId, message: response.message };
  } catch (error) {
    logError('Failed to queue audio upload', { trackId, fileName: file.name, error });
    throw error;
  }
};

export const uploadVideo = async (trackId: string, file: File, onProgress?: (progress: number) => void): Promise<{ uploadId: string, message: string }> => {
  try {
    // Ensure WebSocket connection is established for upload notifications
    const { getAuth } = await import('firebase/auth');
    const { asyncUploadService } = await import('./asyncUploadService');
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      await asyncUploadService.connect(user.uid).catch(err => {
        logError('Failed to establish WebSocket connection for upload notifications', { error: err, userId: user.uid });
        // Continue with upload even if WebSocket fails - user just won't get real-time notifications
      });
    }
    
    const response = await uploadFile(API_CONFIG.ENDPOINTS.UPLOAD_VIDEO(trackId), file, onProgress);
    logInfo('Video upload queued successfully', { trackId, fileName: file.name, uploadId: response.uploadId, message: response.message });
    return { uploadId: response.uploadId, message: response.message };
  } catch (error) {
    logError('Failed to queue video upload', { trackId, fileName: file.name, error });
    throw error;
  }
};

export const uploadPromo = async (trackId: string, file: File, onProgress?: (progress: number) => void): Promise<{ uploadId: string, message: string }> => {
  try {
    // Ensure WebSocket connection is established for upload notifications
    const { getAuth } = await import('firebase/auth');
    const { asyncUploadService } = await import('./asyncUploadService');
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      await asyncUploadService.connect(user.uid).catch(err => {
        logError('Failed to establish WebSocket connection for upload notifications', { error: err, userId: user.uid });
        // Continue with upload even if WebSocket fails - user just won't get real-time notifications
      });
    }
    
    const response = await uploadFile(API_CONFIG.ENDPOINTS.UPLOAD_PROMO(trackId), file, onProgress);
    logInfo('Promo upload queued successfully', { trackId, fileName: file.name, uploadId: response.uploadId, message: response.message });
    return { uploadId: response.uploadId, message: response.message };
  } catch (error) {
    logError('Failed to queue promo upload', { trackId, fileName: file.name, error });
    throw error;
  }
};

export const uploadArtwork = async (trackId: string, file: File, onProgress?: (progress: number) => void): Promise<{ uploadId: string, message: string }> => {
  try {
    // Ensure WebSocket connection is established for upload notifications
    const { getAuth } = await import('firebase/auth');
    const { asyncUploadService } = await import('./asyncUploadService');
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      await asyncUploadService.connect(user.uid).catch(err => {
        logError('Failed to establish WebSocket connection for upload notifications', { error: err, userId: user.uid });
        // Continue with upload even if WebSocket fails - user just won't get real-time notifications
      });
    }
    
    const response = await uploadFile(API_CONFIG.ENDPOINTS.UPLOAD_ARTWORK(trackId), file, onProgress);
    logInfo('Artwork upload queued successfully', { trackId, fileName: file.name, uploadId: response.uploadId, message: response.message });
    return { uploadId: response.uploadId, message: response.message };
  } catch (error) {
    logError('Failed to queue artwork upload', { trackId, fileName: file.name, error });
    throw error;
  }
};

export const uploadFootage = async (trackId: string, file: File, onProgress?: (progress: number) => void): Promise<{ uploadId: string, message: string }> => {
  try {
    // Ensure WebSocket connection is established for upload notifications
    const { getAuth } = await import('firebase/auth');
    const { asyncUploadService } = await import('./asyncUploadService');
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      await asyncUploadService.connect(user.uid).catch(err => {
        logError('Failed to establish WebSocket connection for upload notifications', { error: err, userId: user.uid });
        // Continue with upload even if WebSocket fails - user just won't get real-time notifications
      });
    }
    
    const response = await uploadFile(API_CONFIG.ENDPOINTS.UPLOAD_FOOTAGE(trackId), file, onProgress);
    logInfo('Footage upload queued successfully', { trackId, fileName: file.name, uploadId: response.uploadId, message: response.message });
    return { uploadId: response.uploadId, message: response.message };
  } catch (error) {
    logError('Failed to queue footage upload', { trackId, fileName: file.name, error });
    throw error;
  }
};

// Async upload methods
export const uploadAudioAsync = async (trackId: string, file: File, onProgress?: (progress: number) => void): Promise<{ fileId: string }> => {
  try {
    const { asyncUploadService } = await import('./asyncUploadService');
    const task = await asyncUploadService.uploadFile(trackId, file, 'audio', onProgress);
    const completedTask = await asyncUploadService.waitForCompletion(task.id);
    
    if (completedTask.status === 'completed' && completedTask.file_id) {
      logInfo('Async audio upload completed', { trackId, fileName: file.name, fileId: completedTask.file_id });
      return { fileId: completedTask.file_id };
    } else {
      throw new Error(completedTask.error || 'Upload failed');
    }
  } catch (error) {
    logError('Failed to upload audio file async', { trackId, fileName: file.name, error });
    throw error;
  }
};

export const uploadVideoAsync = async (trackId: string, file: File, onProgress?: (progress: number) => void): Promise<{ fileId: string }> => {
  try {
    const { asyncUploadService } = await import('./asyncUploadService');
    const task = await asyncUploadService.uploadFile(trackId, file, 'video', onProgress);
    const completedTask = await asyncUploadService.waitForCompletion(task.id);
    
    if (completedTask.status === 'completed' && completedTask.file_id) {
      logInfo('Async video upload completed', { trackId, fileName: file.name, fileId: completedTask.file_id });
      return { fileId: completedTask.file_id };
    } else {
      throw new Error(completedTask.error || 'Upload failed');
    }
  } catch (error) {
    logError('Failed to upload video file async', { trackId, fileName: file.name, error });
    throw error;
  }
};

export const uploadPromoAsync = async (trackId: string, file: File, onProgress?: (progress: number) => void): Promise<{ fileId: string }> => {
  try {
    const { asyncUploadService } = await import('./asyncUploadService');
    const task = await asyncUploadService.uploadFile(trackId, file, 'promo', onProgress);
    const completedTask = await asyncUploadService.waitForCompletion(task.id);
    
    if (completedTask.status === 'completed' && completedTask.file_id) {
      logInfo('Async promo upload completed', { trackId, fileName: file.name, fileId: completedTask.file_id });
      return { fileId: completedTask.file_id };
    } else {
      throw new Error(completedTask.error || 'Upload failed');
    }
  } catch (error) {
    logError('Failed to upload promo file async', { trackId, fileName: file.name, error });
    throw error;
  }
};

export const uploadArtworkAsync = async (trackId: string, file: File, onProgress?: (progress: number) => void): Promise<{ fileId: string }> => {
  try {
    const { asyncUploadService } = await import('./asyncUploadService');
    const task = await asyncUploadService.uploadFile(trackId, file, 'artwork', onProgress);
    const completedTask = await asyncUploadService.waitForCompletion(task.id);
    
    if (completedTask.status === 'completed' && completedTask.file_id) {
      logInfo('Async artwork upload completed', { trackId, fileName: file.name, fileId: completedTask.file_id });
      return { fileId: completedTask.file_id };
    } else {
      throw new Error(completedTask.error || 'Upload failed');
    }
  } catch (error) {
    logError('Failed to upload artwork async', { trackId, fileName: file.name, error });
    throw error;
  }
};

export const uploadFootageAsync = async (trackId: string, file: File, onProgress?: (progress: number) => void): Promise<{ fileId: string }> => {
  try {
    const { asyncUploadService } = await import('./asyncUploadService');
    const task = await asyncUploadService.uploadFile(trackId, file, 'footages', onProgress);
    const completedTask = await asyncUploadService.waitForCompletion(task.id);
    
    if (completedTask.status === 'completed' && completedTask.file_id) {
      logInfo('Async footage upload completed', { trackId, fileName: file.name, fileId: completedTask.file_id });
      return { fileId: completedTask.file_id };
    } else {
      throw new Error(completedTask.error || 'Upload failed');
    }
  } catch (error) {
    logError('Failed to upload footage file async', { trackId, fileName: file.name, error });
    throw error;
  }
};

export const updateVideoSocialLinks = async (trackId: string, fileId: string, links: SocialLinks): Promise<void> => {
  await apiClient.patch(API_CONFIG.ENDPOINTS.UPDATE_VIDEO_SOCIAL_LINKS(trackId, fileId), links);
};

export const getArtwork = async (trackId: string): Promise<ImageFile | null> => {
  try {
    const response = await apiClient.get(API_CONFIG.GET_ARTWORK(trackId));
    if (Array.isArray(response.data)) {
      return response.data.length > 0 ? response.data[0] : null;
    }
    return response.data || null;
  } catch (error) {
    logError('Failed to fetch artwork', { trackId, error });
    return null;
  }
};

export const deleteArtwork = async (trackId: string, fileId: string): Promise<void> => {
  try {
    await apiClient.delete(API_CONFIG.ENDPOINTS.DELETE_ARTWORK(trackId, fileId));
    logInfo('Deleted artwork', { trackId, fileId });
  } catch (error) {
    logError('Failed to delete artwork', { trackId, fileId, error });
    throw error;
  }
};

export const deletePromoFile = async (trackId: string, fileId: string): Promise<void> => {
  try {
    await apiClient.delete(API_CONFIG.ENDPOINTS.DELETE_PROMO(trackId, fileId));
    logInfo('Deleted promo file', { trackId, fileId });
  } catch (error) {
    logError('Failed to delete promo file', { trackId, fileId, error });
    throw error;
  }
};

export const syncWithStorage = async (): Promise<void> => {
  try {
    await apiClient.post(API_CONFIG.ENDPOINTS.STORAGE_SYNC);
    logInfo('Storage sync completed successfully');
  } catch (error) {
    logError('Failed to sync with cloud storage', { error });
    throw error;
  }
};

export const syncWithGoogleDrive = async (): Promise<void> => {
  return syncWithStorage();
};

export const importPromoImages = async (targetTrackId: string, sourceTrackId: string): Promise<{ importId: string }> => {
  try {
    const { asyncUploadService } = await import('./asyncUploadService');
    const result = await asyncUploadService.importPromoImages(targetTrackId, sourceTrackId);
    logInfo('Import promo images initiated', { targetTrackId, sourceTrackId, importId: result.importId });
    return result;
  } catch (error) {
    logError('Import promo images failed', { targetTrackId, sourceTrackId, error });
    throw error;
  }
};